/**
 * Spectrum Customizer Integration
 * Provides callback functions required for Spectrum customizer to interact with Shopify cart
 */

// @ts-nocheck - This file intentionally adds properties to window object

// Optional: Store product variant data for SKU lookups
// You can populate this from your product page template
window.spectrumProductVariants = window.spectrumProductVariants || {};

/**
 * Get pricing for a product variant by ID or SKU
 * This function appears to be required by Spectrum customizer (referenced as window.spectrumGetPrice)
 * @param {string|number} identifier - The Shopify variant ID or SKU
 * @returns {Promise<number>} The price in cents
 */
window.spectrumGetPrice = async function(identifier) {
  try {
    console.log('spectrumGetPrice called with:', identifier);
    
    // Check if identifier is a numeric variant ID
    const isNumeric = /^\d+$/.test(String(identifier));
    
    if (isNumeric) {
      // Fetch variant data from Shopify using variant ID
      const response = await fetch(`/variants/${identifier}.js`);
      if (!response.ok) {
        throw new Error(`Failed to fetch variant: ${response.statusText}`);
      }
      const variant = await response.json();
      return variant.price; // Shopify returns price in cents
    } else {
      // Try to look up by SKU in cached product data
      if (window.spectrumProductVariants[identifier]) {
        console.log('Found price for SKU in cache:', identifier);
        return window.spectrumProductVariants[identifier].price;
      }
      
      // If it's a SKU and not in cache, return 0
      console.warn('SKU provided instead of variant ID:', identifier);
      console.warn('Returning 0 - ensure prices are configured in Spectrum or use variant IDs');
      return 0;
    }
  } catch (error) {
    console.error('Error fetching variant price:', error);
    // Return 0 instead of throwing to prevent customizer from breaking
    console.warn('Returning price as 0 due to error');
    return 0;
  }
};

/**
 * Add customized product to cart
 * Called by Spectrum when user clicks "Add to Cart" in the customizer
 * @param {Object} payload - The customization data from Spectrum
 * @returns {Promise}
 */
window.spectrumAddToCart = async function(payload) {
  try {
    console.log('Spectrum Add to Cart payload:', payload);

    // Validate payload
    if (!payload) {
      throw new Error('No payload provided to spectrumAddToCart');
    }

    // Check if SKU exists
    if (!payload.sku) {
      throw new Error('No SKU found in payload. Payload: ' + JSON.stringify(payload));
    }

    // Convert SKU to variant ID
    let variantId = payload.sku;
    let originalVariantId = null;
    let cartVariantId = null; // The variant ID to use for the cart item
    const isNumericId = /^\d+$/.test(String(payload.sku));
    
    // Store original variant ID for linking back to customiser
    if (isNumericId) {
      originalVariantId = payload.sku;
      variantId = payload.sku;
    } else if (window.spectrumProductVariants && window.spectrumProductVariants[payload.sku]) {
      originalVariantId = window.spectrumProductVariants[payload.sku].id;
      variantId = originalVariantId;
    }
    
    // Check if we have a custom variant ID to use for cart (from custom product metafield)
    if (window.spectrumCustomVariantId) {
      console.log('Using custom variant ID for cart (from custom product):', window.spectrumCustomVariantId);
      cartVariantId = window.spectrumCustomVariantId;
      // Keep variantId as the base variant for fetching price data
    } else {
      cartVariantId = variantId;
      
      // If SKU is not numeric and we don't have custom variant, look it up
      if (!isNumericId) {
        console.log('Converting SKU to variant ID:', payload.sku);
        
        if (window.spectrumProductVariants && window.spectrumProductVariants[payload.sku]) {
          cartVariantId = window.spectrumProductVariants[payload.sku].id;
          variantId = cartVariantId;
          console.log('Found variant ID in cache:', cartVariantId);
        } else {
          throw new Error(`Cannot add to cart: SKU "${payload.sku}" not found. Please ensure variant data is loaded on the page.`);
        }
      }
    }

    // Build cart item from Spectrum payload
    const cartItem = {
      id: cartVariantId, // Use custom variant ID if available, otherwise base variant
      quantity: payload.quantity || 1,
      properties: {
        '_spectrum_recipe_id': payload.recipeId || payload.recipeSetId || '',
        '_spectrum_sku': payload.sku, // Store original SKU for reference
        '_spectrum_variant_id': originalVariantId, // Store original variant ID for customiser link
      }
    };
    
    console.log('Adding to cart - Cart Variant ID:', cartVariantId, 'Base Variant ID (for pricing):', variantId, 'SKU:', payload.sku);
    
    // Store custom product ID if available
    if (window.spectrumCustomProductId) {
      cartItem.properties['_spectrum_custom_product_id'] = window.spectrumCustomProductId;
    }
    
    // Store custom variant ID and SKU (these will be used for the actual cart item)
    if (window.spectrumCustomVariantId) {
      cartItem.properties['_spectrum_custom_variant_id'] = window.spectrumCustomVariantId;
      console.log('Storing custom variant ID in properties:', window.spectrumCustomVariantId);
    }
    
    if (window.spectrumProductCustomSku) {
      cartItem.properties['_spectrum_custom_sku'] = window.spectrumProductCustomSku;
      console.log('Storing custom SKU in properties:', window.spectrumProductCustomSku);
    }

    // Fetch base variant to get the correct base price
    // Always use the base variant (variantId) for pricing, not the custom variant
    const variantResponse = await fetch(`/variants/${variantId}.js`);
    if (variantResponse.ok) {
      const variant = await variantResponse.json();
      
      // Store the base variant price in cents for accurate calculations
      let basePrice = variant.price;
      
      // Apply Pro Service discount if applicable (must match price.liquid calculation)
      if (window.spectrumProServiceDiscount && window.spectrumProServiceDiscount > 0) {
        // Match Shopify's discount calculation: round to nearest dollar (half-up)
        const priceDollars = variant.price / 100;
        const discountMultiplier = (100 - window.spectrumProServiceDiscount) / 100;
        const discountedDollars = priceDollars * discountMultiplier;
        const roundedDollars = Math.round(discountedDollars);
        basePrice = roundedDollars * 100;
        console.log('Pro Service discount applied to cart base price:', {
          original: variant.price,
          discounted: basePrice,
          discount: window.spectrumProServiceDiscount + '%'
        });
      }
      
      cartItem.properties['_spectrum_base_price'] = basePrice;
      console.log('Base variant price stored:', basePrice, 'cents');
    }
    
    // Fetch custom variant to get the correct product title (if custom variant exists)
    if (window.spectrumCustomVariantId && cartVariantId !== variantId) {
      const customVariantResponse = await fetch(`/variants/${cartVariantId}.js`);
      if (customVariantResponse.ok) {
        const customVariant = await customVariantResponse.json();
        
        // Use the custom product's title
        let customTitle = customVariant.product_title || customVariant.name || '';
        
        // Remove variant option if present (e.g., " - Urban Grey")
        if (customTitle.includes(' - ')) {
          customTitle = customTitle.split(' - ')[0];
        }
        
        // Remove any existing "Custom" word (case-insensitive) to avoid duplication
        customTitle = customTitle.replace(/\bcustom\b/gi, '').trim();
        
        // Add "Custom" prefix for consistent naming
        customTitle = 'Custom ' + customTitle;
        
        cartItem.properties['_custom_title'] = customTitle;
        console.log('Custom title from custom variant:', customTitle);
      }
    } else {
      // No custom variant, create title from base variant
      const variantResponse2 = await fetch(`/variants/${variantId}.js`);
      if (variantResponse2.ok) {
        const variant = await variantResponse2.json();
        
        let customTitle = variant.product_title || variant.name || '';
        
        // Remove variant option if present (e.g., " - Urban Grey")
        if (customTitle.includes(' - ')) {
          customTitle = customTitle.split(' - ')[0];
        }
        
        // Remove any existing "Custom" word (case-insensitive) to avoid duplication
        customTitle = customTitle.replace(/\bcustom\b/gi, '').trim();
        
        // Add "Custom" prefix for consistent naming
        customTitle = 'Custom ' + customTitle;
        
        cartItem.properties['_custom_title'] = customTitle;
        console.log('Custom title from base variant:', customTitle);
      }
    }

    // Add price information as properties
    // Log pricing details for debugging
    console.log('Spectrum pricing breakdown:', {
      totalPrice: payload.price,
      componentPrice: payload.componentPrice,
      engravingPrice: payload.engravingPrice,
      customFeePrice: payload.customFeePrice
    });
    
    if (payload.price) {
      // Convert dollars to cents for cart transform (e.g., 25.00 -> 2500)
      const priceCents = Math.round(payload.price * 100);
      cartItem.properties['_spectrum_total_price'] = priceCents;
      console.log('Storing _spectrum_total_price (cents):', priceCents);
    }
    if (payload.componentPrice) {
      cartItem.properties['_spectrum_component_price'] = payload.componentPrice;
    }
    if (payload.engravingPrice) {
      cartItem.properties['_spectrum_engraving_price'] = payload.engravingPrice;
    }
    if (payload.customFeePrice) {
      cartItem.properties['_spectrum_custom_fee_price'] = payload.customFeePrice;
    }

    // Add thumbnail URL
    if (payload.thumbnailUrl) {
      cartItem.properties['_spectrum_thumbnail'] = payload.thumbnailUrl;
    }

    // Add custom options if present
    if (payload.options) {
      Object.keys(payload.options).forEach(key => {
        cartItem.properties[key] = payload.options[key];
      });
    }

    // Support legacy items array format if present
    if (payload.items && Array.isArray(payload.items)) {
      console.warn('Legacy items array format detected, using first item');
      const firstItem = payload.items[0];
      if (firstItem.sku) {
        cartItem.id = firstItem.sku;
      }
      if (firstItem.name) {
        cartItem.properties['_spectrum_item_name'] = firstItem.name;
      }
    }

    console.log('Formatted cart item:', cartItem);

    // Add to Shopify cart using Cart API
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartItem)
    });

    if (!response.ok) {
      let errorMessage = 'Failed to add to cart';
      try {
        const errorData = await response.json();
        errorMessage = errorData.description || errorData.message || errorMessage;
        console.error('Cart API error response:', errorData);
      } catch (e) {
        console.error('Could not parse error response');
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Successfully added to cart:', result);
    
    // Debug: Fetch variant to check actual price
    const debugVariant = await fetch(`/variants/${variantId}.js`);
    if (debugVariant.ok) {
      const varData = await debugVariant.json();
      console.log('Variant price check:', {
        variantId: variantId,
        priceInCents: varData.price,
        priceInDollars: varData.price / 100
      });
    }

    // Fetch updated cart to get total item count
    const cartResponse = await fetch('/cart.json');
    const cart = await cartResponse.json();
    
    console.log('Fetched cart data:', cart);
    console.log('Cart item count:', cart.item_count);

    // Update cart blip directly
    const cartBlip = document.querySelector('.cart-blip');
    if (cartBlip) {
      cartBlip.textContent = cart.item_count;
      if (cart.item_count > 0) {
        cartBlip.removeAttribute('hidden');
        cartBlip.closest('.header-item__link')?.classList.add('has-blip-visible');
      }
      console.log('Updated cart blip to show', cart.item_count, 'items');
    }

    // Also update cart notification if present
    const cartNotificationTotal = document.querySelector('#cart-notification-link .cart-total');
    if (cartNotificationTotal) {
      cartNotificationTotal.textContent = cart.item_count;
    }

    // Refresh cart drawer contents
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.renderAndShowItems === 'function') {
      console.log('Refreshing cart drawer contents');
      cartItems.renderAndShowItems();
    } else {
      console.log('cart-items element not found or renderAndShowItems method not available');
    }

    // Dispatch cart:updated event for cart mixing prevention
    const cartUpdatedEvent = new CustomEvent('cart:updated');
    document.dispatchEvent(cartUpdatedEvent);
    console.log('Dispatched cart:updated event');

    return result;
  } catch (error) {
    console.error('Error adding Spectrum item to cart:', error);
    alert(`Failed to add item to cart: ${error.message}`);
    throw error;
  }
};

/**
 * Get prices and availability for items
 * Called by Spectrum to display pricing in the customizer UI
 * @param {Array<string>} itemsBySkuOrName - Array of SKUs or product names
 * @returns {Promise<Array>}
 */
window.spectrumItemPricesAndAvailabilityCallback = async function(itemsBySkuOrName) {
  try {
    const results = [];

    for (const identifier of itemsBySkuOrName) {
      try {
        // Try to fetch variant info if identifier is a variant ID
        const variantId = identifier;
        const response = await fetch(`/variants/${variantId}.js`);
        
        if (response.ok) {
          const variant = await response.json();
          results.push({
            sku: identifier,
            name: variant.name || variant.title,
            type: 'baseProduct',
            price: variant.price / 100, // Display expects dollars, not cents
            listPrice: variant.compare_at_price ? variant.compare_at_price / 100 : null,
            isoCurrency: window.Shopify?.currency?.active || 'USD',
            inStock: variant.available,
            options: null
          });
        } else {
          // Fallback for items that can't be found
          results.push({
            sku: identifier,
            name: identifier,
            type: 'baseProduct',
            price: 0,
            listPrice: null,
            isoCurrency: window.Shopify?.currency?.active || 'USD',
            inStock: true,
            options: null
          });
        }
      } catch (err) {
        console.warn(`Could not fetch price for ${identifier}:`, err);
        // Return default structure
        results.push({
          sku: identifier,
          name: identifier,
          type: 'baseProduct',
          price: 0,
          listPrice: null,
          isoCurrency: window.Shopify?.currency?.active || 'USD',
          inStock: true,
          options: null
        });
      }
    }

    return results;
  } catch (error) {
    console.error('Error fetching item prices:', error);
    throw error;
  }
};

/**
 * Calculate total price for customized product
 * Called by Spectrum to display the total price in real-time
 * @param {Array} items - Array of items with SKU and quantity
 * @returns {Promise<Object>}
 */
window.spectrumTotalPriceCallback = async function(items) {
  try {
    let totalPrice = 0;

    for (const item of items) {
      try {
        // Fetch variant price
        const response = await fetch(`/variants/${item.sku}.js`);
        if (response.ok) {
          const variant = await response.json();
          totalPrice += (variant.price / 100) * item.quantity; // Display expects dollars, not cents
        }
      } catch (err) {
        console.warn(`Could not fetch price for ${item.sku}:`, err);
      }
    }

    return {
      price: totalPrice,
      isoCurrency: window.Shopify?.currency?.active || 'USD',
      options: null
    };
  } catch (error) {
    console.error('Error calculating total price:', error);
    throw error;
  }
};

/**
 * Generate cart item data
 * Alternative method that returns the payload without adding to cart
 * @returns {Promise<Object>}
 */
window.spectrumGenerateCartItem = async function() {
  // This is called by Spectrum and returns a promise with cart item data
  // Implementation depends on how you want to handle this
  return new Promise((resolve) => {
    // Spectrum will populate this with the customization data
    resolve({});
  });
};

console.log('Spectrum integration callbacks initialized');
